import * as React from 'react';
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import {
	Box,
	Button,
	Card,
	CardActionArea,
	CircularProgress,
	Container,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Cathena from '../../assets/images/cathena.png';
import PieChartOutlinedIcon from '@mui/icons-material/PieChartOutlined';
import AlignVerticalBottomOutlinedIcon from '@mui/icons-material/AlignVerticalBottomOutlined';
import { useAccount, useSignMessage } from 'wagmi';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { styled } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { getAuthNonce, IChatThreadResponse, storeChatEntry } from 'apis';
import { authActions } from 'store/auth';
import { RootState } from 'store';
import MarkdownRenderer, { OnActionType } from 'pages/Chat/MarkdownRender';
import { AUTH_TOKEN, MOBILE_BREAK_POINTS } from 'data/constant';

type IconComponent = React.ComponentType<SvgIconProps>;

type QuantModeProps = {
	title?: string;
	subtitle?: string;
	avatarSrc?: string;
	suggestions?: Suggestion[];
	onSelectSuggestion?: (s: Suggestion) => void;
};

type Suggestion = {
	id: string;
	Icon: IconComponent;
	title: string;
	caption: string;
	onClick?: () => void;
};

const MessagesContainer = styled('div')({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	maxWidth: '100%',
	height: 'calc(100vh - 75px)',
	overflowY: 'auto',
	padding: '20px 0 140px 0',
	scrollBehavior: 'smooth',
	'&::-webkit-scrollbar': {
		width: '0px',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: 'rgba(0,0,0,0.2)',
		borderRadius: '3px',
	},

	[`@media (max-width: ${MOBILE_BREAK_POINTS}px)`]: {
		padding: '40px 0 140px 0',
	},
});

const MessageBubble = styled('div', {
	shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
	maxWidth: '80%',

	padding: '10px 15px',
	borderRadius: '10px',
	alignSelf: isUser ? 'flex-end' : 'flex-start',
	backgroundColor: isUser ? '#1C4ED8' : '#fff',
	color: isUser ? '#fff' : '#000',
	wordBreak: 'break-word',
	lineHeight: 1.4,

	'@media (max-width: 700px)': {
		maxWidth: '95%',
		width: 'fit-content',
		minWidth: '30%',
	},
}));

const TypingIndicator = styled('div')({
	display: 'flex',
	alignItems: 'center',
	padding: '8px 16px',
	borderRadius: '18px',
	backgroundColor: '#f0f0f0',
	alignSelf: 'flex-start',
	marginBottom: '8px',
});

const InputContainer = styled('div')({
	position: 'absolute',
	bottom: 0,
	left: '50%',
	transform: 'translateX(-50%)',
	backgroundColor: 'transparent',
	display: 'flex',
	justifyContent: 'center',
	zIndex: 100,
	padding: '15px 24px',
	maxWidth: 1200,
	width: '100%',

	'@media (max-width: 800px)': {
		padding: '15px 24px',
	},

	[`@media (max-width: ${MOBILE_BREAK_POINTS}px)`]: {
		padding: '15px 16px',
	},
});

const PrimaryButton = styled(Button)(() => ({
	backgroundColor: '#29292c',
	whiteSpace: 'nowrap',
	color: '#FFFFFF',
	padding: '12px 50px',
	fontFamily: 'Inter, sans-serif',
	borderRadius: 15,
	fontWeight: 600,
	fontSize: '14px',
	letterSpacing: '0.15em',
	textTransform: 'uppercase',
	boxShadow: 'none',
	'&:hover': {
		backgroundColor: '#4d4d52',
		boxShadow: 'none',
	},
	'&:disabled': {
		backgroundColor: '#818089',
		boxShadow: 'none',
	},
}));

const PrimaryTextField = styled(TextField)(() => ({
	'& .MuiOutlinedInput-root': {
		borderRadius: 15,
		backgroundColor: '#29292c',
		color: '#FFFFFF',
		padding: 0,
		'& fieldset': { borderColor: '#29292c' },
		'&:hover fieldset': { borderColor: '#4d4d52' },
		'&.Mui-focused fieldset': { borderColor: '#4d4d52' },
		'&.Mui-disabled': { backgroundColor: '#818089' },

		'& .MuiInputBase-input': {
			textAlign: 'left',
		},
		'& .MuiInputBase-inputMultiline': {
			textAlign: 'left',
			padding: '12px 14px',
			lineHeight: 1.5,
			fontSize: 16,
		},
	},
	'& textarea::placeholder': {
		color: '#b9b9be',
		opacity: 1,
		lineHeight: 1.5,
		fontSize: 16,
	},
}));

const DEFAULT_SUGGESTIONS: Suggestion[] = [
	{
		id: 'long-btc',
		Icon: TrendingUpIcon,
		title: 'Long 1 BTC',
		caption: 'Open a long position',
		onClick: () => {},
	},
	{
		id: 'portfolio',
		Icon: PieChartOutlinedIcon,
		title: 'Show my portfolio',
		caption: 'View balance and positions',
		onClick: () => {},
	},
	{
		id: 'signals',
		Icon: AlignVerticalBottomOutlinedIcon,
		title: 'Latest signals',
		caption: 'Get market analysis',
		onClick: () => {},
	},
];

const TypingText: React.FC<{ text: string; onComplete?: () => void }> = ({
	text,
	onComplete,
}) => {
	const [displayed, setDisplayed] = useState('');

	useEffect(() => {
		let index = 0;
		setDisplayed('');
		const interval = setInterval(() => {
			if (index < text.length) {
				setDisplayed(text.slice(0, index + 1));
				index++;
			} else {
				clearInterval(interval);
				onComplete?.();
			}
		}, 11);

		return () => clearInterval(interval);
	}, [text, onComplete]);

	return <MarkdownRenderer text={displayed} />;
};

const SuggestionCard = React.memo(function SuggestionCard({
	Icon,
	title,
	caption,
	onClick,
}: {
	Icon: IconComponent;
	title: string;
	caption: string;
	onClick?: () => void;
}) {
	return (
		<Card
			variant="outlined"
			sx={{ borderRadius: 2, '&:hover': { bgcolor: '#e3e7ee' } }}
		>
			<CardActionArea onClick={onClick} aria-label={title}>
				<Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
					<Box sx={{ mr: 1.5, mt: 0.25 }}>
						<Icon fontSize="small" style={{ color: '#5774c9' }} />
					</Box>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
						}}
					>
						<Typography variant="body2" fontWeight={600}>
							{title}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{caption}
						</Typography>
					</Box>
				</Box>
			</CardActionArea>
		</Card>
	);
});

const StableTextField = React.memo(function StableTextField({
	value,
	onChange,
	onEnter,
	loading,
	placeholder,
	disabled,
}: {
	value: string;
	onChange: (v: string) => void;
	onEnter: () => void;
	loading: boolean;
	placeholder: string;
	disabled?: boolean;
}) {
	const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(
		null,
	);
	const selectionRef = useRef<{ start: number; end: number } | null>(null);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const target = e.target;
			selectionRef.current = {
				start: target.selectionStart ?? value.length,
				end: target.selectionEnd ?? value.length,
			};
			onChange(target.value);
		},
		[onChange, value.length],
	);

	useLayoutEffect(() => {
		const sel = selectionRef.current;
		const el = inputRef.current as
			| HTMLTextAreaElement
			| HTMLInputElement
			| null;
		if (el && sel) {
			const len = value.length;
			const start = Math.min(sel.start, len);
			const end = Math.min(sel.end, len);
			try {
				el.setSelectionRange(start, end);
			} catch {
				//
			}
		}
	}, [value]);

	return (
		<PrimaryTextField
			inputRef={(node) => {
				inputRef.current = node as any;
			}}
			autoFocus
			value={value}
			onChange={handleChange}
			onKeyDown={(e) => {
				if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault();
					onEnter();
				}
			}}
			disabled={disabled}
			fullWidth
			variant="outlined"
			placeholder={placeholder}
			multiline
			rows={3}
			maxRows={3}
			sx={{
				'& .MuiOutlinedInput-root': {
					backgroundColor: '#fff',
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: '#000',
					},
					'&:hover .MuiOutlinedInput-notchedOutline': {
						borderColor: '#000',
					},
					'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: '#000',
						borderWidth: 2,
					},
				},
				'& .MuiOutlinedInput-input, & .MuiOutlinedInput-inputMultiline':
					{
						color: '#000',
						paddingRight: '64px',
					},
			}}
			InputProps={{
				endAdornment: (
					<InputAdornment position="end" sx={{ mr: '20px' }}>
						<IconButton
							onClick={onEnter}
							disabled={loading || !value.trim()}
							sx={{
								backgroundColor: '#000',
								'&:hover': { backgroundColor: '#444' },
								color: '#fff',
							}}
							aria-label="Send message"
						>
							{loading ? (
								<CircularProgress size={24} color="inherit" />
							) : (
								<ArrowUpwardIcon />
							)}
						</IconButton>
					</InputAdornment>
				),
			}}
		/>
	);
});

const RenderInput = React.memo(function RenderInput({
	value,
	setValue,
	onSend,
	loading,
}: {
	value: string;
	setValue: (v: string) => void;
	onSend: () => void;
	loading: boolean;
}) {
	return (
		<InputContainer>
			<Box sx={{ width: '100%', direction: 'ltr' }}>
				<StableTextField
					disabled={true}
					value={value}
					onChange={setValue}
					onEnter={onSend}
					loading={loading}
					placeholder="Message..."
				/>
			</Box>
		</InputContainer>
	);
});

export function QuantMode({ suggestions, onSelectSuggestion }: QuantModeProps) {
	const [inputText, setInputText] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [isTyping, setIsTyping] = useState(false);
	const dispatch = useDispatch();
	const { signMessageAsync } = useSignMessage();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const { isConnected, address } = useAccount();
	const [loading, setLoading] = useState(false);
	const [messages, setMessages] = useState<IChatThreadResponse[]>([]);
	const appState = useSelector((state: RootState) => state.auth);
	const token = localStorage.getItem('token') || appState.loginResponse.token;

	const scrollToBottom = useCallback(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({
				behavior: 'smooth',
			});
		}
	}, []);

	const handleClick = useCallback(
		(s: Suggestion) => {
			if (!isConnected) {
				dispatch(authActions.openWallet(true));
			} else if (onSelectSuggestion) {
				onSelectSuggestion(s);
			}
		},
		[dispatch, isConnected, onSelectSuggestion],
	);

	const onLogin = useCallback(async () => {
		try {
			setIsLoading(true);
			const res = await getAuthNonce(address || '');
			dispatch(authActions.setAuthMessage(res));
			const signature = await signMessageAsync({
				message: res.authMessage,
			});
			/*const authRes = await authSignUpLogin({
				message: res.authMessage,
				signature,
			});*/
			localStorage.setItem('token', AUTH_TOKEN);
			/*localStorage.setItem('appTId', authRes.id);
			dispatch(
				appActions.setLogin({
					id: authRes.id,
					wallet: authRes.wallet,
					token: authRes.token,
				}),
			);*/
		} catch (e) {
			//
		} finally {
			setIsLoading(false);
		}
	}, [address, dispatch, signMessageAsync]);

	const onSend = useCallback(async () => {
		if (!inputText.trim() || loading) return;
		try {
			setLoading(true);

			const userMessage: IChatThreadResponse = {
				query: inputText,
				response: '',
				thread_id: messages[0]?.thread_id || '',
				processing_time: '',
				success: false,
			};
			setMessages((prev) => [...prev, userMessage]);
			setInputText('');
			if (messages.length === 0)
				dispatch(authActions.setTodayHistory(userMessage.query));

			const chatResponse = await storeChatEntry({
				wallet_address: address as string,
				query: inputText,
				thread_id:
					messages.length > 0
						? messages[messages.length - 1].thread_id
						: '',
			});

			/*const res = await getChatThread(
				userMessage.query,
				messages.length > 0 ? messages[0].thread_id : undefined,
			);*/
			setMessages((prev) => [...prev.slice(0, -1), chatResponse]);
			//setIsTyping(true);
		} finally {
			setLoading(false);
		}
	}, [inputText, loading, messages, dispatch, address]);

	const onActions: OnActionType = useCallback(
		async (actionId, data, label, raw) => {
			if (!data?.trim() || loading) return;
			try {
				setLoading(true);

				const userMessage: IChatThreadResponse = {
					query: label ?? '',
					response: '',
					thread_id: messages[messages.length - 1]?.thread_id || '',
					processing_time: '',
					success: false,
				};
				setMessages((prev) => [...prev, userMessage]);
				setInputText('');
				scrollToBottom();
				if (messages.length === 0)
					dispatch(authActions.setTodayHistory(userMessage.query));

				const chatResponse = await storeChatEntry({
					wallet_address: address as string,
					query: data,
					thread_id:
						messages.length > 0
							? messages[messages.length - 1].thread_id
							: '',
				});

				/*const res = await getChatThread(
				userMessage.query,
				messages.length > 0 ? messages[0].thread_id : undefined,
			);*/
				setMessages((prev) => [
					...prev.slice(0, -1),
					{ ...chatResponse, query: label || '' },
				]);
				setIsTyping(true);
				scrollToBottom();
			} catch (e: any) {
				//
			} finally {
				setLoading(false);
			}
		},
		[loading, messages, scrollToBottom, dispatch, address],
	);

	//const handleTypingComplete = () => setIsTyping(false);

	const renderMessage = useCallback(
		(message: IChatThreadResponse, index: number) => {
			//const isLast = index === messages.length - 1;

			return (
				<React.Fragment key={`${message.thread_id}-${index}`}>
					<Box minHeight={index !== 0 ? 80 : 20} />
					<MessageBubble isUser>
						<Typography
							variant="subtitle2"
							fontSize={12}
							margin={0}
							fontWeight={400}
							color={'#fff'}
						>
							{message.query}
						</Typography>
					</MessageBubble>
					<Box minHeight={30} />

					{message.processing_time && (
						<Typography
							variant="caption"
							fontSize={12}
							sx={{
								opacity: 0.6,
								mt: 1,
								display: 'block',
								mb: 1,
							}}
						>
							Thought for {message.processing_time} seconds
						</Typography>
					)}
					{/*{isLast && isTyping ? (
						<MemoizedTypingText
							text={message.response}
							onComplete={handleTypingComplete}
						/>
					) : (*/}
					<>
						{message.response && (
							<MessageBubble
								isUser={false}
								style={{ borderRadius: 20 }}
							>
								<MarkdownRenderer
									onAction={onActions}
									text={message.response}
									isCurrent={messages.length - 1 === index}
								/>
							</MessageBubble>
						)}
					</>

					{/*)}*/}
				</React.Fragment>
			);
		},
		[messages.length, onActions],
	);

	useEffect(() => {
		if (appState?.loginResponse?.address === '') {
			setMessages([]);
		}
	}, [appState?.loginResponse?.address]);

	return (
		<Box
			sx={{
				minHeight: 'calc(100vh - 75px)',
				bgcolor: '#f7fafb',
				display: 'flex',
				alignItems: 'center',
				width: '100%',
				position: 'relative',
			}}
		>
			<Container>
				{messages.length === 0 && (
					<Stack spacing={3} alignItems="center" textAlign="center">
						<img
							alt="Cathena"
							src={Cathena}
							width={96}
							height={96}
							loading="lazy"
						/>
						<Box>
							<Typography variant="h5" fontWeight={700}>
								{'Ready to Start Trading'}
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mt: 1, lineHeight: 1.6 }}
							>
								{
									"I'm Cathena, your AI trading assistant. I can help you execute trades, analyze markets, and manage your portfolio using natural language commands."
								}
							</Typography>
						</Box>

						{!isConnected && (
							<Box sx={{ width: '100%', mt: 1 }}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: 'block', mb: 1 }}
								>
									Try these commands:
								</Typography>
								<Stack spacing={1}>
									{(suggestions ?? DEFAULT_SUGGESTIONS).map(
										(s) => (
											<SuggestionCard
												key={s.id}
												Icon={s.Icon}
												title={s.title}
												caption={s.caption}
												onClick={() => handleClick(s)}
											/>
										),
									)}
								</Stack>
							</Box>
						)}

						{isConnected &&
							(token ? (
								<RenderInput
									value={inputText}
									setValue={setInputText}
									onSend={onSend}
									loading={loading}
								/>
							) : (
								<PrimaryButton
									onClick={onLogin}
									size="small"
									disabled={isLoading}
								>
									{isLoading ? 'Authorizing…' : 'Login'}
								</PrimaryButton>
							))}
					</Stack>
				)}

				{isConnected && token && messages.length !== 0 && (
					<>
						<MessagesContainer>
							{messages.map((message, index) =>
								renderMessage(message, index),
							)}
							{loading && !isTyping && (
								<TypingIndicator>
									<CircularProgress
										size={16}
										sx={{ mr: 1 }}
									/>
									<Typography variant="body2">
										Cathena is Thinking...
									</Typography>
								</TypingIndicator>
							)}
							<div ref={messagesEndRef} />
						</MessagesContainer>

						<RenderInput
							value={inputText}
							setValue={setInputText}
							onSend={onSend}
							loading={loading}
						/>
					</>
				)}
			</Container>
		</Box>
	);
}
